import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart2, LogOut, Menu, X, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import InstallButton from './InstallButton';
import { fetchStorageUsage } from '../lib/r2Storage';
import clsx from 'clsx';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [storage, setStorage] = useState<{ used: number; total: number; percentage: number } | null>(null);

  const navigation = [
    { name: 'Entries', href: '/calendar', icon: BarChart2 },
    { name: 'Statistics', href: '/stats', icon: BarChart2 },
    { name: 'Search', href: '/search', icon: Search },
  ];

  const handleLogoClick = () => {
    navigate('/', { replace: true });
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('refresh-calendar'));
    }, 100);
  };

  useEffect(() => {
    async function loadStorage() {
      try {
        const usage = await fetchStorageUsage();
        setStorage(usage);
      } catch (err) {
        console.error('Storage usage fetch failed:', err);
      }
    }
    loadStorage();
    const interval = setInterval(loadStorage, 300000);
    return () => clearInterval(interval);
  }, []);

  return (
    <header className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <div 
              className="flex-shrink-0 cursor-pointer py-2"
              onClick={handleLogoClick}
            >
              <span className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'sans-serif' }}>
                <span className="text-[#4a3f35]">
                  <span className="text-4xl">F</span>ARM
                </span>
                <span className="text-[#8cc63f]">
                  <span className="text-4xl">A</span>RK
                </span>
              </span>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={clsx(
                        isActive
                          ? 'bg-[#8cc63f]/10 text-[#4a3f35]'
                          : 'text-gray-600 hover:text-[#4a3f35] hover:bg-[#8cc63f]/5',
                        'px-3 py-2 rounded-md text-sm font-medium flex items-center'
                      )}
                    >
                      <item.icon className="h-4 w-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            {storage && (
              <div className="flex items-center space-x-2" title={`${(storage.used / 1024 / 1024 / 1024).toFixed(2)} GB used`}>
                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#8cc63f] rounded-full"
                    style={{ width: `${Math.min(storage.percentage, 100)}%` }}
                  />
                </div>
                <span className="text-xs text-[#4a3f35] whitespace-nowrap">
                  {(storage.used / 1024 / 1024 / 1024).toFixed(1)} / {(storage.total / 1024 / 1024 / 1024).toFixed(0)} GB
                </span>
              </div>
            )}
            <InstallButton />
            <span className="text-sm text-gray-600">
              {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : ''}
            </span>
            <button
              onClick={() => signOut()}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    isActive
                      ? 'bg-[#8cc63f]/10 text-[#4a3f35]'
                      : 'text-gray-600 hover:text-[#4a3f35] hover:bg-[#8cc63f]/5',
                    'block px-3 py-2 rounded-md text-base font-medium flex items-center'
                  )}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
            <div className="border-t border-gray-200 pt-4 pb-3">
              <div className="flex items-center px-3">
                <div className="text-base font-medium text-gray-800">
                  {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : ''}
                </div>
              </div>
              <div className="mt-3 px-2 space-y-1">
                <InstallButton />
                <button
                  onClick={() => {
                    signOut();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 w-full"
                >
                  <LogOut className="h-5 w-5 mr-3" />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}